import React, {useMemo, useState} from 'react';
import {
    Box,
    Button,
    CircularProgress,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import {useGetTrainingCourseReportQuery} from '../../../api/services/trainingCourseService';
import {downloadTrainingCourseReport} from '../../../api/services/filesService';
import useFileDownloader from '../../../utility/hooks/useFileDownloader';
import {quizLessons, reportRows} from '../../../help/course/trainingReport';

const ALL = 'all';

/**
 * Super Admins see each school's staff training course progress, and download it (AD-2667).
 * Staff who have not opened the course are listed as 0, so a school that has not started shows up.
 */
const TrainingProgress = () => {
    const {data: staff = [], isLoading, isError} = useGetTrainingCourseReportQuery();
    const [school, setSchool] = useState(ALL);
    const download = useFileDownloader();

    const rows = useMemo(() => reportRows(staff), [staff]);
    const schools = useMemo(
        () => [...new Map(rows.map(row => [row.schoolId, row.school])).entries()].sort((a, b) => a[1].localeCompare(b[1])),
        [rows]
    );
    const shown = school === ALL ? rows : rows.filter(row => row.schoolId === school);
    const scoreColumns = quizLessons().map(lesson => lesson.title);

    const exportRows = shown.map(row => ({
        school: row.school,
        firstName: row.firstName,
        lastName: row.lastName,
        email: row.email,
        roles: row.roles,
        topicsOpened: row.topicsOpened,
        lastTopicOpened: row.lastTopicOpened,
        lastSignedIn: row.lastSignedIn,
        lessonsCompleted: row.lessonsCompleted,
        scores: row.scores
    }));

    if (isLoading) {
        return <Box sx={{py: 6, textAlign: 'center'}}><CircularProgress/></Box>;
    }

    if (isError) {
        return <Typography sx={{py: 4}} color="error">The training progress could not be loaded.</Typography>;
    }

    return (
        <Box sx={{py: 3}}>
            <Stack direction={{xs: 'column', sm: 'row'}} gap={2} alignItems={{sm: 'center'}} sx={{mb: 2}}>
                <Typography variant="h5" sx={{flexGrow: 1}}>Training progress</Typography>
                <TextField
                    select
                    size="small"
                    label="School"
                    value={school}
                    onChange={event => setSchool(event.target.value)}
                    sx={{minWidth: 260}}
                    data-testid="training-progress-school"
                >
                    <MenuItem value={ALL}>All schools</MenuItem>
                    {schools.map(([id, name]) => <MenuItem key={id} value={id}>{name}</MenuItem>)}
                </TextField>
                <Button
                    variant="outlined"
                    startIcon={<FileDownloadRoundedIcon/>}
                    disabled={shown.length === 0}
                    onClick={() => download(downloadTrainingCourseReport, {format: 'csv', rows: exportRows})}
                >
                    Download CSV
                </Button>
                <Button
                    variant="outlined"
                    startIcon={<FileDownloadRoundedIcon/>}
                    disabled={shown.length === 0}
                    onClick={() => download(downloadTrainingCourseReport, {format: 'xlsx', rows: exportRows})}
                >
                    Download XLSX
                </Button>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                {shown.length} staff, {shown.filter(row => row.opened === 0).length} have not opened the course
            </Typography>
            <TableContainer component={Paper} variant="outlined">
                <Table size="small" data-testid="training-progress-table">
                    <TableHead>
                        <TableRow>
                            <TableCell>School</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Role</TableCell>
                            <TableCell>Topics opened</TableCell>
                            <TableCell>Last topic opened</TableCell>
                            <TableCell>Last signed in</TableCell>
                            <TableCell>Lessons completed</TableCell>
                            {scoreColumns.map(title => <TableCell key={title}>{title}</TableCell>)}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {shown.map(row => (
                            <TableRow key={`${row.schoolId}-${row.email}`}>
                                <TableCell>{row.school}</TableCell>
                                <TableCell>{row.firstName} {row.lastName}</TableCell>
                                <TableCell>{row.email}</TableCell>
                                <TableCell>{row.roles}</TableCell>
                                <TableCell>{row.topicsOpened}</TableCell>
                                <TableCell>{row.lastTopicOpened}</TableCell>
                                <TableCell>{row.lastSignedIn}</TableCell>
                                <TableCell>{row.lessonsCompleted}</TableCell>
                                {scoreColumns.map(title => <TableCell key={title}>{row.scores[title]}</TableCell>)}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default TrainingProgress;
